// API Client — typed fetch wrapper with auth

const BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * Resolve a backend path against an API origin.
 *
 * The frontend (Cloudflare Pages, helloconvo.ai) and backend (api.helloconvo.ai)
 * are different origins: a relative backend path used for navigation, href,
 * src, or a raw fetch lands on the SPA shell, not the backend. The backend
 * keeps emitting origin-free paths (it does not know which origin a client
 * reaches it through); every browser-facing use resolves them here.
 *
 * Only backend paths are accepted. Anything else is rejected rather than
 * passed through, so this can never turn into an open redirect.
 */
export function resolveApiUrl(path: string, baseUrl: string): string {
  if (!(path === "/api" || path.startsWith("/api/") || path.startsWith("/api?"))) {
    throw new Error(`Expected a backend path starting with /api/, got: ${path}`);
  }
  return `${baseUrl.replace(/\/+$/, "")}${path}`;
}

/** Absolute backend URL for a `/api/...` path, using the configured API origin. */
export function apiUrl(path: string): string {
  return resolveApiUrl(path, BASE_URL);
}

/** Keep a broken API connection from leaving an operator on an indefinite spinner. */
export function withClientTimeout<T>(operation: (signal: AbortSignal) => Promise<T>, timeoutMs = 15_000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const timer = globalThis.setTimeout(() => {
      controller.abort();
      reject(new Error("This request is taking too long. Please try again."));
    }, timeoutMs);
    void operation(controller.signal).then(
      (value) => { globalThis.clearTimeout(timer); resolve(value); },
      (error) => { globalThis.clearTimeout(timer); reject(error); },
    );
  });
}

export function isDevProxyMissResponse(
  status: number,
  bodyText: string,
  contentType: string | null,
): boolean {
  return (
    status === 404 &&
    (contentType?.includes("text/html") ?? false) &&
    /Cannot\s+[A-Z]+\s+\/api\//.test(bodyText)
  );
}

export function parseApiJsonBody<T>(status: number, bodyText: string): T {
  if (bodyText.trim() === "") {
    throw new ApiError(
      status,
      "Request succeeded but returned an empty body",
      "INVALID_RESPONSE",
    );
  }
  try {
    return JSON.parse(bodyText) as T;
  } catch {
    throw new ApiError(
      status,
      "Request succeeded but returned invalid JSON",
      "INVALID_RESPONSE",
    );
  }
}

export function parseApiSuccessBody<T>(status: number, bodyText: string): T {
  if (status === 204 || status === 205) {
    return undefined as T;
  }
  return parseApiJsonBody<T>(status, bodyText);
}

export async function fetchDevProxyFallback<T>(
  fetchImpl: typeof fetch,
  path: string,
  options: RequestInit,
  fallbackBaseUrl: string,
): Promise<T> {
  const fallbackRes = await fetchImpl(`${fallbackBaseUrl}${path}`, options);
  if (fallbackRes.status === 401) {
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  const fallbackText = await fallbackRes.text().catch(() => "");
  if (fallbackRes.ok) {
    return parseApiSuccessBody<T>(fallbackRes.status, fallbackText);
  }

  const errorBody = (() => {
    try {
      return JSON.parse(fallbackText || "{}");
    } catch {
      return { error: { message: "Request failed", code: "UNKNOWN" } };
    }
  })();
  throw new ApiError(
    fallbackRes.status,
    errorBody.error?.message || "Request failed",
    errorBody.error?.code || "UNKNOWN",
  );
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private buildUrl(path: string, overrideBaseUrl?: string): string {
    return `${overrideBaseUrl ?? this.baseUrl}${path}`;
  }

  private isDevMode(): boolean {
    return Boolean(import.meta.env.DEV);
  }

  private devFallbackBaseUrl(): string | null {
    if (!this.isDevMode() || this.baseUrl) return null;
    if (typeof window === "undefined") return null;
    const apiPort = import.meta.env.VITE_API_PORT || "4819";
    return `${window.location.protocol}//${window.location.hostname}:${apiPort}`;
  }

  private isDevProxyMiss(status: number, bodyText: string, contentType: string | null): boolean {
    return (
      this.isDevMode() &&
      !this.baseUrl &&
      isDevProxyMissResponse(status, bodyText, contentType)
    );
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    const url = this.buildUrl(path);
    const options: RequestInit = {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...extraHeaders,
      },
      signal,
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const res = await fetch(url, options);

    if (res.status === 401) {
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const responseText = await res.text().catch(() => "");
      const contentType = res.headers.get("content-type");
      const fallbackBaseUrl = this.devFallbackBaseUrl();
      let fallbackStatus: number | null = null;
      let fallbackText = "";
      if (fallbackBaseUrl && this.isDevProxyMiss(res.status, responseText, contentType)) {
        try {
          return await fetchDevProxyFallback<T>(fetch, path, options, fallbackBaseUrl);
        } catch (error) {
          if (error instanceof ApiError) {
            fallbackStatus = error.status;
            fallbackText = JSON.stringify({ error: { message: error.message, code: error.code } });
          } else {
            fallbackStatus = 502;
            fallbackText = JSON.stringify({
              error: {
                message: error instanceof Error ? error.message : "Fallback request failed",
                code: "NETWORK_ERROR",
              },
            });
          }
        }
      }

      const errorText = fallbackStatus !== null ? fallbackText : responseText;
      const errorBody = (() => {
        try {
          return JSON.parse(errorText || "{}");
        } catch {
          return { error: { message: "Request failed", code: "UNKNOWN" } };
        }
      })();
      throw new ApiError(
        fallbackStatus ?? res.status,
        errorBody.error?.message || "Request failed",
        errorBody.error?.code || "UNKNOWN",
        errorBody.error,
      );
    }

    const successText = await res.text().catch(() => "");
    return parseApiSuccessBody<T>(res.status, successText);
  }

  async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>("GET", path, undefined, undefined, signal);
  }

  /**
   * Binary GET with the session cookie (e.g. kit font files). A simple CORS request: no JSON
   * headers, and no login redirect on 401 — callers treat failures as "use the fallback".
   */
  async getBytes(path: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    const res = await fetch(this.buildUrl(path), { method: "GET", credentials: "include", signal });
    if (!res.ok) throw new ApiError(res.status, `Request failed (${res.status})`, "HTTP_ERROR");
    return res.arrayBuffer();
  }

  async post<T>(
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>("POST", path, body, extraHeaders, signal);
  }

  async put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>("PUT", path, body, undefined, signal);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>("DELETE", path);
  }

  async upload<T>(path: string, formData: FormData, onProgress?: (progress: number) => void): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    return await new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || !onProgress) return;
        onProgress(Math.min(1, Math.max(0, event.loaded / event.total)));
      };

      xhr.onerror = () => {
        reject(new ApiError(0, "Upload failed", "NETWORK_ERROR"));
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          window.location.href = "/login";
          reject(new Error("Unauthorized"));
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          let errorBody: { error?: { message?: string; code?: string } } = {};
          try {
            errorBody = JSON.parse(xhr.responseText || "{}");
          } catch {
            errorBody = { error: { message: "Upload failed", code: "UNKNOWN" } };
          }
          reject(new ApiError(
            xhr.status,
            errorBody.error?.message || "Upload failed",
            errorBody.error?.code || "UNKNOWN",
          ));
          return;
        }

        resolve(parseApiSuccessBody<T>(xhr.status, xhr.responseText || ""));
      };

      xhr.send(formData);
    });
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const api = new ApiClient(BASE_URL);
